import React from 'react';
import styled from 'styled-components';
import { shimmerFill } from '../../../motion';

/**
 * Full-page shimmer placeholder for the homepage while a mobile OAuth
 * sign-in finalizes (`AuthGateContext.redirectPending`). During that window
 * the restored ANON session is on screen but about to be replaced — showing
 * either homepage layout would be wrong for a beat (marketing hero for a
 * user who just signed in, or an empty boards grid). The skeleton reads as
 * "we know you, your stuff is coming" instead (Kate 2026-07-06).
 *
 * Shape-matched to the signed-in homepage (wordmark row → Coming-Up strip →
 * BrowseMyFriendsSection tile grid: 2/3/4 columns, 4:5 tiles) so the real
 * layout swaps in without a jump. The fill is the shared `shimmerFill` —
 * one shimmer style across every loading surface.
 *
 * This skeleton's own band-over-warm-grey recipe (verified against live pixels
 * 2026-09-02, after a `background-position` sweep over `var(--muted)` painted
 * as a flat block here) is what THE-808 promoted to the shared helper, so the
 * only change on this surface is 1.3s → `--dur-shimmer`.
 */

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.color.creamLight};
  font-family: ${({ theme }) => theme.font.sans};
`;

// Mirrors SiteHeader's wordmark row. The wordmark is real (it's static
// brand chrome, not loading data) so the page still reads as Thea.
const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  @media (min-width: 768px) {
    padding: 16px 40px;
  }
`;

// Real wordmark (matches SiteHeader's) rather than a shimmer bar — the page
// the user just left had a real wordmark top-left, so keeping it makes the
// skeleton read as the same site loading, not everything vanishing.
const Wordmark = styled.span`
  font-family: ${({ theme }) => theme.font.serif};
  font-style: italic;
  letter-spacing: 0.025em;
  font-size: 28px;
  color: ${({ theme }) => theme.color.clay};
`;

const Content = styled.div`
  padding: 8px 16px 40px;
  @media (min-width: 768px) {
    padding: 8px 40px 40px;
  }
`;

// Coming-Up strip silhouette.
const StripBar = styled.div`
  height: 72px;
  border-radius: 16px;
  margin: 12px 0 24px;
  ${shimmerFill}
`;

// BrowseMyFriendsSection grid silhouette: same breakpoints + tile ratio.
const TileGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  @media (min-width: 640px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const Tile = styled.div`
  aspect-ratio: 4 / 5;
  border-radius: 16px;
  ${shimmerFill}
`;

export const HomeSkeleton: React.FC = () => (
  <Page data-testid="home-skeleton" aria-busy="true" aria-label="Signing you in">
    <TopRow>
      <Wordmark>thea</Wordmark>
    </TopRow>
    <Content>
      <StripBar />
      <TileGrid>
        {Array.from({ length: 6 }, (_, i) => (
          <Tile key={i} />
        ))}
      </TileGrid>
    </Content>
  </Page>
);
